/**
 * FIELD LAYOUT — the admin-editable layer on top of lib/formSchema.js.
 *
 * formSchema.js stays the base structure shipped in code. `settings/fieldLayout` in
 * Firestore layers three things on top of it, with no deploy:
 *
 *   overrides[path]  visibility, position, page, label/help/requirement and the preventClone
 *                    flag for a field that already exists in formSchema.js
 *   customFields[]   brand-new fields an admin invented, appended into an existing
 *                    section at a chosen position
 *   customSections[] brand-new pages an admin invented, which can contain either
 *                    custom fields or movable base fields
 *   sectionOverrides title, position and visibility for any merged section
 *
 * A firm with no fieldLayout document (or an empty one) gets exactly the code defaults,
 * so this is transparent until someone actually customises something.
 *
 * Everything here is pure — no Firestore, no React — so the merge and the
 * preventClone stripping are unit-testable in isolation (see fieldLayout.test.js).
 */

export const DEFAULT_FIELD_LAYOUT = {
  version: 1,
  overrides: {},
  customFields: [],
  customSections: [],
  sectionOverrides: {},
};

/** Path prefix for admin-invented fields, so they can never collide with real matter data. */
export const CUSTOM_PATH_PREFIX = 'custom.';

/** A stable storage-safe name for an admin-created attachment bucket. */
export function attachmentCategorySlug(value) {
  return String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/** New attachment buckets may not collide with any existing category, including critical ones. */
export function isUniqueAttachmentCategory(category, existingCategories = []) {
  const normalized = attachmentCategorySlug(category);
  return Boolean(normalized) && !new Set(existingCategories.map(attachmentCategorySlug)).has(normalized);
}

/** ID prefix for admin-invented pages, kept distinct from code-shipped section IDs. */
export const CUSTOM_SECTION_PREFIX = 'custom.page.';

/**
 * The field types an admin may pick for a custom field. Every one of these already has a
 * `case` branch in components/Field.jsx — the builder deliberately cannot invent a type
 * the renderer does not understand.
 */
export const CUSTOM_FIELD_TYPES = [
  ['text', 'Text'],
  ['textarea', 'Paragraph'],
  ['yesno', 'Yes / No'],
  ['date', 'Date'],
  ['select', 'Dropdown'],
  ['radio', 'Choice (pick one)'],
  ['multicheck', 'Checkbox list'],
  ['number', 'Number'],
  ['percent', 'Percentage'],
  ['email', 'Email'],
  ['tel', 'Phone'],
  ['money', 'Money'],
  ['userSelect', 'People (pick one)'],
  ['peopleMultiSelect', 'People (multiple)'],
  ['optionsMultiSelect', 'Custom list (multiple)'],
  ['address', 'Address (street, city, state, ZIP)'],
  ['clientSearch', 'Client (from the client directory)'],
  ['notice', 'Notice banner'],
  ['attachments', 'Attachments'],
  ['repeatable', 'Repeating group'],
];

/** Only these custom controls need administrator-defined option rows. */
export function customFieldTypeNeedsOptions(type) {
  return ['select', 'radio', 'multicheck', 'optionsMultiSelect'].includes(type);
}

/** Only 'percent' carries admin-tunable numeric bounds (see percentParams below). */
export function customFieldTypeNeedsRange(type) {
  return type === 'percent';
}

/**
 * Retypes an existing CUSTOM field in place, so changing e.g. Text -> Dropdown doesn't
 * require deleting and recreating the field (which would change its path, orphan every
 * saved answer, and silently break any visibleIf/approval condition or PDF layout row
 * pointing at the old path).
 *
 * Keys that only make sense for the OLD type are dropped, and keys the NEW type needs to
 * be usable at all are seeded: an option control with no options renders an empty picker,
 * and a repeating group with no columns renders a "no columns yet" placeholder with no Add
 * button — a field the submitter cannot fill in either way. Everything type-neutral
 * (path, label, help, requirement, visibleIf, width, format, order, section) is preserved
 * verbatim.
 *
 * NOT applicable to base/code-defined fields: their type comes from formSchema.js, and the
 * stored answers plus every reader (PDF, manifest, conditions) are written against that
 * shape. Retyping one from the admin panel would fork the schema from its data.
 */
export function changeCustomFieldType(field, nextType) {
  if (!field || !nextType || field.type === nextType) return field;

  const {
    options: _options,
    fields: _fields,
    addLabel: _addLabel,
    category: _category,
    text: _text,
    variant: _variant,
    min: _min,
    max: _max,
    decimals: _decimals,
    placeholder: _placeholder,
    ...typeNeutral
  } = field;

  const next = { ...typeNeutral, type: nextType };
  if (customFieldTypeNeedsOptions(nextType)) {
    next.options = Array.isArray(field.options) && field.options.length ? field.options : [];
  }
  if (customFieldTypeNeedsSubFields(nextType)) {
    next.fields = Array.isArray(field.fields) ? field.fields : [];
    if (field.addLabel) next.addLabel = field.addLabel;
  }
  if (nextType === 'notice') {
    next.text = field.text || '';
    if (field.variant) next.variant = field.variant;
  }
  if (nextType === 'attachments') {
    next.category = field.category || attachmentCategorySlug(field.label || field.path || 'other');
  }
  // A placeholder is meaningful for any control with an unselected state, so it survives a
  // change between two such types rather than being retyped away.
  if (field.placeholder) next.placeholder = field.placeholder;
  return next;
}

export const PERCENT_DEFAULTS = { min: 0, max: 100, decimals: 0 };

/**
 * Normalizes a percentage field's admin-set bounds. Any missing/malformed piece falls back
 * to 0–100 with whole numbers, so a half-configured field is always usable rather than
 * un-fillable. An inverted range (min above max) is repaired by swapping, not rejected —
 * a validation error an admin can only see by reading the console would just look like a
 * broken field to the person filling out the form.
 */
export function percentParams(field) {
  const num = (v, fallback) => (typeof v === 'number' && Number.isFinite(v) ? v : fallback);
  let min = num(field?.min, PERCENT_DEFAULTS.min);
  let max = num(field?.max, PERCENT_DEFAULTS.max);
  if (min > max) [min, max] = [max, min];
  const rawDecimals = num(field?.decimals, PERCENT_DEFAULTS.decimals);
  const decimals = Math.min(4, Math.max(0, Math.round(rawDecimals)));
  return { min, max, decimals };
}

/**
 * Bring a typed percentage inside the field's configured range, and round it to the configured
 * number of decimals.
 *
 * Called on BLUR, never on each keystroke, and that is the whole design. A number input lets you
 * type anything — `min`/`max` only bind the stepper arrows and a native form submit, which this
 * app never does — but clamping as you type makes some values unreachable: in a 10–100 field,
 * typing the "5" of "50" would snap to 10 the instant it was pressed, and the next keypress would
 * make 100. Clamping when the field is left lets any intermediate state be typed and still
 * guarantees what is stored is in range.
 *
 * Returns null for an empty value, so clearing a percentage stays cleared rather than becoming
 * the minimum.
 */
export function clampPercent(value, field) {
  if (value === '' || value === null || value === undefined) return null;
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  const { min, max, decimals } = percentParams(field);
  const factor = 10 ** decimals;
  return Math.round(Math.min(max, Math.max(min, number)) * factor) / factor;
}

/** A repeating group owns a list of sub-fields instead of an option list. */
export function customFieldTypeNeedsSubFields(type) {
  return type === 'repeatable';
}

/**
 * The column types allowed inside a repeating group. Deliberately a SUBSET of
 * CUSTOM_FIELD_TYPES: no nested repeatables (a group inside a group has no sane storage
 * or rendering story), no notices, and no attachments (uploads are keyed by category on
 * the matter, not per row).
 */
export const SUB_FIELD_TYPES = [
  ['text', 'Text'],
  ['textarea', 'Paragraph'],
  ['number', 'Number'],
  ['money', 'Money'],
  ['date', 'Date'],
  ['email', 'Email'],
  ['tel', 'Phone'],
  ['yesno', 'Yes / No'],
  ['checkbox', 'Checkbox'],
  ['select', 'Dropdown'],
  ['radio', 'Choice (pick one)'],
  ['tags', 'Tags'],
];

const SUB_FIELD_TYPE_VALUES = new Set(SUB_FIELD_TYPES.map(([value]) => value));

/** Sub-field types that need administrator-defined choices. */
export function subFieldTypeNeedsOptions(type) {
  return ['select', 'radio'].includes(type);
}

/**
 * A sub-field's `path` is a KEY WITHIN ITS ROW (rows are plain objects, see the
 * Repeatable control), not a dotted matter path — so it is slugged and de-duplicated
 * against its siblings only.
 */
export function subFieldKey(label, takenKeys = []) {
  const slug =
    String(label || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .slice(0, 40)
      // Trim AFTER slicing. Trimming first lets a 40-char truncation end in "_", and
      // re-slugging that key later would strip it — silently changing the key and
      // orphaning every saved row stored under it. This function must be idempotent:
      // subFieldKey(subFieldKey(x)) === subFieldKey(x).
      .replace(/^_+|_+$/g, '') || 'column';
  const taken = new Set(takenKeys);
  let candidate = slug;
  let n = 2;
  while (taken.has(candidate)) {
    candidate = `${slug}_${n}`;
    n += 1;
  }
  return candidate;
}

/**
 * Normalizes an admin-edited sub-field list before it is persisted: trims, drops rows
 * without a label, assigns/keeps a stable row key, drops unknown types, de-duplicates
 * keys, and sanitizes the option rows of choice columns. A repeating group whose columns
 * are malformed would otherwise render a broken (or crashing) control on the live form.
 */
export function sanitizeSubFieldRows(rows) {
  if (!Array.isArray(rows)) return [];
  const usable = rows.filter(
    (row) => row && typeof row === 'object' && String(row.label ?? '').trim()
  );
  // TWO PASSES. Every key that ALREADY exists is reserved first, so a column that has not
  // been assigned a key yet can never claim a key an existing column is already storing
  // data under (which would bump the existing one to `key_2` and orphan its rows).
  const seen = new Set(
    usable.map((row) => (row.path ? subFieldKey(row.path) : null)).filter(Boolean)
  );
  const clean = [];
  for (const row of usable) {
    const label = String(row.label).trim();
    const type = SUB_FIELD_TYPE_VALUES.has(row.type) ? row.type : 'text';
    // An existing key is kept verbatim (idempotent slug); only a key-less column derives
    // a fresh one, de-duplicated against everything already reserved.
    const key = row.path ? subFieldKey(row.path) : subFieldKey(label, [...seen]);
    seen.add(key);
    const sub = { path: key, label, type };
    if (subFieldTypeNeedsOptions(type)) {
      const options = sanitizeOptionRows(row.options);
      // A choice column with no usable choices would render an empty dropdown; fall back
      // to a plain text column rather than shipping a dead control.
      if (options.length === 0) {
        clean.push({ path: key, label, type: 'text' });
        continue;
      }
      sub.options = options;
    }
    clean.push(sub);
  }
  return clean;
}

/**
 * Matter keys that describe where a matter sits in its lifecycle rather than what the
 * matter *is*. A clone or a template must never carry these over, regardless of any
 * preventClone flag: the copy is a brand-new draft that has not been submitted, has no
 * numbers, and owns none of the original's uploaded files.
 */
export const LIFECYCLE_FIELDS = [
  'id',
  'status',
  'createdBy',
  'createdAt',
  'updatedAt',
  'updatedBy',
  'submittedAt',
  'submittedBy',
  'clientNumber',
  'matterNumber',
  'numbersAssignedAt',
  'approvalChain',
  'approvalStatus',
  'currentGroupIndex',
  'rejectedAt',
  'rejectedBy',
  'reopenedAt',
  'reopenedBy',
  'collaboratorUids',
  'softRequiredBypasses',
  // Frozen at submission and belonging to THAT submission: a clone must not inherit a
  // waiver its new submitter never gave, nor the disclosure permission granted with it.
  'softRequiredWaivers',
  'softRequiredWaiverDisclosure',
  // Stamped per submission too (see lib/workflowAccess.js) — a copy has not been submitted,
  // so it has not been through, or around, the workflow.
  'workflowEnabled',
  'attachments',
];

/**
 * Overrides are keyed by field path, so every base field must have a stable, unique path
 * before it can be individually addressed. Notices and attachment blocks now have synthetic
 * paths for layout purposes; uploads themselves remain keyed by each attachment's category.
 */
export function isOverridable(field) {
  return Boolean(field && field.path);
}

/** Tolerates a missing, partial or malformed document. */
export function normalizeLayout(layout) {
  const overrides =
    layout && typeof layout.overrides === 'object' && !Array.isArray(layout.overrides)
      ? layout.overrides
      : {};
  const sectionOverrides =
    layout && typeof layout.sectionOverrides === 'object' && !Array.isArray(layout.sectionOverrides)
      ? layout.sectionOverrides
      : {};
  return {
    version: Number.isFinite(layout?.version) ? layout.version : 1,
    overrides,
    customFields: Array.isArray(layout?.customFields) ? layout.customFields : [],
    customSections: Array.isArray(layout?.customSections) ? layout.customSections : [],
    sectionOverrides,
  };
}

function mergeBaseField(field, index, sectionId, override) {
  const merged = {
    ...field,
    sectionId,
    isCustom: false,
    isActive: true,
    order: index,
    preventClone: false,
    verificationRequired: false,
  };
  if (!override) return merged;
  return {
    ...merged,
    order: Number.isFinite(override.order) ? override.order : index,
    // Ignore an invalid destination rather than losing a base field from the form. The
    // grouping pass validates section IDs again, which keeps partial/malformed settings safe.
    sectionId: typeof override.sectionId === 'string' && override.sectionId ? override.sectionId : sectionId,
    isActive: override.isActive !== false,
    preventClone: override.preventClone === true,
    verificationRequired: override.verificationRequired === true,
    ...(override.label ? { label: override.label } : null),
    ...(override.text ? { text: override.text } : null),
    ...(override.variant ? { variant: override.variant } : null),
    ...(override.visibleIf !== undefined ? { visibleIf: override.visibleIf } : null),
    // `false` intentionally overrides a code-shipped true default, so admins can undo
    // a layout recommendation without redeploying. An absent override keeps the schema default.
    ...(override.newRow !== undefined ? { newRow: override.newRow === true } : null),
    // Keep one effective width value for the renderer and Field Builder. A stored false
    // deliberately wins over a code-authored colSpan: 2; without an override, colSpan
    // remains the backwards-compatible shipped default.
    fullWidth: override.fullWidth !== undefined
      ? override.fullWidth === true
      : field.fullWidth === true || field.colSpan === 2,
    // The three-bucket width ('full' | 'half' | 'third') supersedes fullWidth when set.
    // An admin override wins over a code-shipped width; neither erases the other's
    // fullWidth/colSpan fallback, which fieldGrid.js still honors when width is absent.
    ...(override.width ? { width: override.width } : null),
    // Visual emphasis (bold label, background, border — see lib/fieldFormat.js).
    ...(override.format ? { format: override.format } : null),
    // Admin-retuned choices for a base option field. Guarded: only when the code schema
    // ships a static options array. Fields fed by a live source (optionsFrom — file
    // subtypes, areas of law, roster) are managed on their own admin pages, and letting a
    // stale layout override shadow those would silently fork the source of truth.
    ...(Array.isArray(override.options) && Array.isArray(field.options) && !field.optionsFrom
      ? { options: override.options }
      : null),
    // Admin-edited columns for a BASE repeating group (custom repeatables already own
    // theirs) — e.g. removing "Is an entity" from Opposing parties. Guarded to a non-empty
    // array so a malformed override can never strip every column and leave a dead control;
    // clearing the override restores the code-shipped columns. Mirrored in
    // functions/src/documents/effectiveLayout.js so the printed record shows the same
    // columns the form did.
    ...(field.type === 'repeatable' && Array.isArray(override.fields) && override.fields.length
      ? { fields: override.fields, fieldsOverridden: true }
      : null),
    // Limit a people chooser's list to roster titles ("Legal Secretary", …) — see
    // lib/peopleTitles.js for the token-matching rules. An empty/absent array is the
    // historical unfiltered chooser.
    ...(Array.isArray(override.titleFilter) && override.titleFilter.length
      ? { titleFilter: override.titleFilter }
      : null),
    // The Add-button label on a control that grows rows (repeating group, origination
    // split) — relabelable now that these sections get repurposed ("Add originating
    // attorney" vs the shipped "Add working attorney").
    ...(override.addLabel ? { addLabel: override.addLabel } : null),
    // requirementOverride / helpOverride rather than requirement / help: the rules engine
    // decides the final value, and a layout override has to beat settings/fieldRules
    // without silently erasing it.
    ...(override.helpText ? { helpOverride: override.helpText } : null),
    ...(override.requirement ? { requirementOverride: override.requirement } : null),
    // The unselected-state prompt on a dropdown/search control ("Select attorney…"). An
    // empty string is treated as "no override" rather than a blank prompt, which would
    // leave a dropdown's first row visually empty and unexplained.
    ...(override.placeholder ? { placeholder: override.placeholder } : null),
    // A percentage field's admin-tunable bounds. Each is passed through independently so
    // setting only one (e.g. max: 50) keeps the other defaults — percentParams() resolves
    // the final effective values wherever they're actually used.
    ...(typeof override.min === 'number' ? { min: override.min } : null),
    ...(typeof override.max === 'number' ? { max: override.max } : null),
    ...(typeof override.decimals === 'number' ? { decimals: override.decimals } : null),
    // "Show it but do not let them edit it", and the condition that decides when. Named
    // disabledOverride for the same reason as requirementOverride: the rules engine picks the
    // final value, and a layout override has to beat settings/fieldRules without erasing it.
    ...(typeof override.disabled === 'boolean' ? { disabledOverride: override.disabled } : null),
    ...(override.disabledIf !== undefined ? { disabledIf: override.disabledIf } : null),
    // A starting value, and the condition under which it applies.
    ...(override.defaultValue !== undefined ? { defaultValue: override.defaultValue } : null),
    ...(override.defaultIf !== undefined ? { defaultIf: override.defaultIf } : null),
  };
}

function mergeCustomField(field, fallbackOrder, sectionId) {
  return {
    path: field.path,
    label: field.label || field.path,
    type: field.type || 'text',
    ...(Array.isArray(field.options) ? { options: field.options } : null),
    ...(field.category ? { category: field.category } : null),
    ...(field.text ? { text: field.text } : null),
    ...(field.variant ? { variant: field.variant } : null),
    ...(field.helpText ? { helpOverride: field.helpText } : null),
    ...(field.requirement ? { requirementOverride: field.requirement } : null),
    // Unlike a base-field override, a custom field owns its complete definition in
    // customFields[]. Preserve the condition verbatim so the visual editor's JSON
    // round-trips through Firestore and into the effective intake schema.
    ...(field.visibleIf !== undefined ? { visibleIf: field.visibleIf } : null),
    // Same passthrough for the two conditions a custom field can carry: when it is greyed out,
    // and when its starting value applies.
    ...(typeof field.disabled === 'boolean' ? { disabledOverride: field.disabled } : null),
    ...(field.disabledIf !== undefined ? { disabledIf: field.disabledIf } : null),
    ...(field.defaultValue !== undefined ? { defaultValue: field.defaultValue } : null),
    ...(field.defaultIf !== undefined ? { defaultIf: field.defaultIf } : null),
    ...(field.colSpan === 2 ? { colSpan: 2 } : null),
    ...(field.newRow === true ? { newRow: true } : null),
    ...(field.fullWidth === true || field.colSpan === 2 ? { fullWidth: true } : null),
    ...(field.width ? { width: field.width } : null),
    ...(field.format ? { format: field.format } : null),
    // A repeating group carries its own column definitions and Add-button label. The
    // Repeatable control renders `fields` directly, so this passthrough is what makes an
    // admin-created group work with no renderer change.
    ...(Array.isArray(field.fields) ? { fields: field.fields } : null),
    ...(field.addLabel ? { addLabel: field.addLabel } : null),
    ...(field.placeholder ? { placeholder: field.placeholder } : null),
    // A people picker's roster-title filter (lib/peopleTitles.js) — who this chooser offers.
    ...(Array.isArray(field.titleFilter) && field.titleFilter.length
      ? { titleFilter: field.titleFilter }
      : null),
    // Percentage bounds — see percentParams() for how a partial set resolves.
    ...(typeof field.min === 'number' ? { min: field.min } : null),
    ...(typeof field.max === 'number' ? { max: field.max } : null),
    ...(typeof field.decimals === 'number' ? { decimals: field.decimals } : null),
    sectionId,
    isCustom: true,
    isActive: field.isActive !== false,
    order: Number.isFinite(field.order) ? field.order : fallbackOrder,
    preventClone: field.preventClone === true,
    verificationRequired: field.verificationRequired === true,
  };
}

function mergeSections(baseSections, layout, { includeInactive }) {
  const { overrides, customFields, customSections, sectionOverrides } = normalizeLayout(layout);
  const baseSectionIds = new Set(baseSections.map((section) => section.id));

  const sections = baseSections
    .map((section, index) => {
      const override = sectionOverrides[section.id] || {};
      return {
        ...section,
        title: override.title || section.title,
        // An admin-edited description wins; an explicit empty string intentionally
        // removes a code-shipped description rather than falling back to it.
        ...(override.description !== undefined ? { description: override.description } : null),
        // Page-level show/hide logic. An admin override REPLACES the code-shipped
        // condition, and `true` means "always show" — which is how a firm recovers a page
        // whose coded condition references a field they have since removed (e.g. the
        // Litigation page, gated in code on matterType.isLitigation).
        ...(override.visibleIf !== undefined ? { visibleIf: override.visibleIf } : null),
        order: Number.isFinite(override.order) ? override.order : index,
        isActive: override.isActive !== false,
        isCustom: false,
        _tie: index,
      };
    });

  const seenSectionIds = new Set(baseSectionIds);
  customSections.forEach((custom, index) => {
    // A malformed Firestore document must not be able to duplicate or impersonate a
    // code-defined page. Custom pages are deliberately namespaced, and code pages remain
    // removable only by retirement through their existing section override.
    if (
      !custom ||
      typeof custom.id !== 'string' ||
      !custom.id.startsWith(CUSTOM_SECTION_PREFIX) ||
      seenSectionIds.has(custom.id)
    ) {
      return;
    }
    seenSectionIds.add(custom.id);
    const override = sectionOverrides[custom.id] || {};
    const fallbackOrder = baseSections.length + index;
    sections.push({
      id: custom.id,
      title: override.title || custom.title || 'Untitled page',
      ...(override.visibleIf !== undefined
        ? { visibleIf: override.visibleIf }
        : custom.visibleIf !== undefined
          ? { visibleIf: custom.visibleIf }
          : null),
      ...(override.description !== undefined
        ? { description: override.description }
        : custom.description !== undefined
          ? { description: custom.description }
          : null),
      order: Number.isFinite(override.order)
        ? override.order
        : Number.isFinite(custom.order)
          ? custom.order
          : fallbackOrder,
      isActive: override.isActive !== undefined ? override.isActive !== false : custom.isActive !== false,
      isCustom: true,
      fields: [],
      _tie: fallbackOrder,
    });
  });

  sections.sort((a, b) => a.order - b.order || a._tie - b._tie);
  const sectionIds = new Set(sections.map((section) => section.id));

  const fieldsBySection = new Map(sections.map((section) => [section.id, []]));
  let tie = 0;

  for (const section of baseSections) {
    section.fields.forEach((field, index) => {
      const override = isOverridable(field) ? overrides[field.path] : null;
      // A DELETED base field is dropped from BOTH the effective form and the builder
      // view — unlike a retired (isActive: false) field, which the builder still shows
      // dimmed. Recovery is the Field Builder's "Deleted fields" panel (deletedBaseFields
      // below); the code schema means the definition itself can never be lost.
      if (override && override.deleted === true) return;
      const merged = mergeBaseField(field, index, section.id, override);
      // Only overridable base fields can move. A stale/invalid destination falls back to the
      // coded page so a malformed saved layout can never drop a base field.
      const destination =
        isOverridable(field) && sectionIds.has(merged.sectionId) ? merged.sectionId : section.id;
      fieldsBySection.get(destination).push({ field: { ...merged, sectionId: destination }, tie });
      tie += 1;
    });
  }

  for (const custom of customFields) {
    if (!custom || !custom.path || !sectionIds.has(custom.sectionId)) continue;
    const fallbackOrder = fieldsBySection.get(custom.sectionId).length;
    fieldsBySection
      .get(custom.sectionId)
      .push({ field: mergeCustomField(custom, fallbackOrder, custom.sectionId), tie });
    tie += 1;
  }

  return sections
    .filter((section) => includeInactive || section.isActive)
    .map(({ _tie, ...section }) => ({
      ...section,
      fields: fieldsBySection
        .get(section.id)
        .sort((a, b) => a.field.order - b.field.order || a.tie - b.tie)
        .map((entry) => entry.field)
        .filter((field) => includeInactive || field.isActive),
    }));
}

/**
 * The schema the intake form actually renders: base sections with inactive fields removed,
 * surviving fields in their admin-chosen order, plus custom sections and custom fields
 * injected into their chosen section at their order position.
 */
export function buildEffectiveSections(baseSections, layout) {
  return mergeSections(baseSections, layout, { includeInactive: false });
}

/**
 * Same merge, but keeps deactivated custom/code sections and fields so the admin builder can
 * show — and re-enable — them. Only the builder should use this.
 */
export function buildBuilderSections(baseSections, layout) {
  return mergeSections(baseSections, layout, { includeInactive: true });
}

/** Flat list of every real (non-notice) effective field — the ALL_FIELDS equivalent. */
export function effectiveFieldList(sections) {
  return sections.flatMap((section) =>
    section.fields
      .filter((f) => f.path && f.type !== 'notice' && f.type !== 'attachments')
      .map((f) => ({ ...f, sectionId: section.id, sectionTitle: section.title }))
  );
}

/**
 * Field definitions consumable by the generic ConditionBuilder. This deliberately starts
 * from the builder schema (rather than a second, hand-maintained field list), so it sees
 * base fields plus every custom field on every page, including pages/fields an admin has
 * temporarily retired and may later restore.
 *
 * The approval-flow editor intentionally continues to use its small, curated
 * BUILDER_FIELDS list. Only field-visibility editing calls this helper.
 */
export function conditionBuilderFieldList(baseSections, layout) {
  const seen = new Set();
  return effectiveFieldList(buildBuilderSections(baseSections, layout))
    .filter((field) => field.type !== 'attachments')
    // A repeating group's value is an array of row OBJECTS. The visual builder can only
    // express single/multi/boolean leaves, so offering one here produced `in`/`nin`
    // conditions that could never match. Conditions over row contents use the `some`
    // operator, which is authored in the raw-JSON editor.
    .filter((field) => field.type !== 'repeatable')
    // An address is an OBJECT at one path. The visual builder can only express single/multi/
    // boolean leaves, so offering it would produce conditions that can never match. A condition
    // on one PART of an address (`custom.billing_address.state` eq 'CA') still works — it is a
    // plain string path — and is written in the raw-JSON editor.
    .filter((field) => field.type !== 'address')
    .filter((field) => {
      if (seen.has(field.path)) return false;
      seen.add(field.path);
      return true;
    })
    .map((field) => {
      const kind =
        field.type === 'yesno' || field.type === 'checkbox'
          ? 'boolean'
          : field.type === 'multicheck' ||
              field.type === 'practiceGroupMultiSelect' ||
              field.type === 'peopleMultiSelect' ||
              field.type === 'optionsMultiSelect'
            ? 'multi'
            : 'single';
      const options =
        Array.isArray(field.options)
          ? field.options
              .filter((option) => option && option.value !== undefined)
              .map((option) => ({ id: option.value, name: option.label || String(option.value) }))
          : undefined;
      const optionsFrom =
        field.type === 'practiceGroupSelect' ? 'practiceGroups' :
          field.type === 'areaOfLawSelect' ? 'areasOfLaw' : null;
      return {
        path: field.path,
        label: `${field.sectionTitle} — ${field.label}`,
        kind,
        // The control's own type, and the directory a picker picks from. `kind` deliberately
        // flattens twenty types into three for the condition builder; an auto-fill rule needs to
        // know that a field is specifically a `clientSearch` and which feed it draws on, which
        // that flattening throws away.
        type: field.type,
        ...(field.feedId ? { feedId: field.feedId } : null),
        ...(options ? { options } : null),
        optionsFrom,
      };
    });
}

/**
 * Deep-clones plain objects and arrays, and passes anything else (Firestore Timestamps,
 * Dates) through by reference. structuredClone would flatten a Timestamp into a bare
 * object; JSON round-tripping would destroy it outright.
 */
function deepClone(value) {
  if (Array.isArray(value)) return value.map(deepClone);
  if (value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype) {
    const out = {};
    for (const [key, inner] of Object.entries(value)) out[key] = deepClone(inner);
    return out;
  }
  return value;
}

/** Deletes a dotted path in place. Missing intermediate objects are simply a no-op. */
function unsetPath(target, path) {
  const parts = String(path).split('.');
  let node = target;
  for (let i = 0; i < parts.length - 1; i += 1) {
    if (!node || typeof node !== 'object') return;
    node = node[parts[i]];
  }
  if (node && typeof node === 'object') delete node[parts[parts.length - 1]];
}

/**
 * Removes every field the admin flagged `preventClone` from a copy of a matter.
 *
 * This is the single shared implementation behind BOTH "Clone this matter" and "Save as
 * template" — one flag, one behaviour. Templates apply it in the browser before the data
 * ever reaches the saveTemplate callable, so flagged values never leave the client.
 *
 * @param {object} matterData      a full matter document
 * @param {Array}  effectiveFields the field list to honor flags from — pass
 *                                 clonePolicyFieldList(), NOT the effective form list:
 *                                 a hidden or DELETED field's saved answers still exist
 *                                 on matters, and its "never copy" flag must keep
 *                                 protecting them.
 * @returns {object} a deep copy with the flagged paths unset
 */
export function stripPreventCloneFields(matterData, effectiveFields = []) {
  const copy = deepClone(matterData || {});
  for (const field of effectiveFields) {
    if (field && field.preventClone === true && field.path) unsetPath(copy, field.path);
  }
  return copy;
}

/**
 * The field list clone/template stripping must run against: EVERY base field (including
 * hidden and deleted ones — their overrides may carry preventClone, and their saved
 * answers survive removal from the form) plus every stored custom field. A sensitive
 * field must not leak into clones/templates merely because an admin later removed it
 * from the layout.
 */
export function clonePolicyFieldList(baseSections, layout) {
  const { overrides, customFields } = normalizeLayout(layout);
  const fields = [];
  for (const section of baseSections) {
    for (const field of section.fields) {
      if (!field.path) continue;
      const override = overrides[field.path];
      fields.push({
        path: field.path,
        preventClone: override ? override.preventClone === true : false,
      });
    }
  }
  for (const custom of customFields) {
    if (custom && custom.path) {
      fields.push({ path: custom.path, preventClone: custom.preventClone === true });
    }
  }
  return fields;
}

/** Drops the lifecycle-only keys listed in LIFECYCLE_FIELDS. Used by clone and templates. */
export function stripLifecycleFields(matterData) {
  const copy = deepClone(matterData || {});
  for (const key of LIFECYCLE_FIELDS) delete copy[key];
  return copy;
}

/**
 * Human-readable rendering of a field's current value for the pre-submit verification
 * modal (see IntakeForm.jsx). Pure and presentation-only — it never decides whether a
 * field needs verifying, only how to display whatever value it already has.
 *
 *   - arrays are comma-joined (empty array → "—")
 *   - yesno values render as Yes / No
 *   - null/undefined/empty string → "—"
 *   - everything else is stringified as-is
 */
export function formatFieldValueForDisplay(field, value, roster = []) {
  if (field?.type === 'yesno') {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    return '—';
  }
  if ((field?.type === 'userSelect' || field?.type === 'personSearch') && typeof value === 'string' && value) {
    const person = (Array.isArray(roster) ? roster : []).find((p) => p.id === value);
    return person ? person.displayName || person.email || value : value;
  }
  if (field?.type === 'peopleMultiSelect' && Array.isArray(value)) {
    const namesById = new Map(
      (Array.isArray(roster) ? roster : []).map((person) => [
        person.id,
        person.displayName || person.email || person.id,
      ])
    );
    const joined = value
      .map((id) => namesById.get(id) || String(id))
      .filter(Boolean)
      .join(', ');
    return joined || '—';
  }
  if (field?.type === 'optionsMultiSelect' && Array.isArray(value)) {
    const labelsByValue = new Map(
      (Array.isArray(field.options) ? field.options : []).map((option) => [option.value, option.label])
    );
    const joined = value
      .map((v) => labelsByValue.get(v) || String(v))
      .filter(Boolean)
      .join(', ');
    return joined || '—';
  }
  // A repeating group's value is an array of row OBJECTS. Render each row as its column
  // values joined, so the verification modal shows real content instead of "[object
  // Object]". Column labels come from the field definition when available.
  if (field?.type === 'repeatable' && Array.isArray(value)) {
    const columns = Array.isArray(field.fields) ? field.fields : [];
    // Each cell is rendered as "Column label: value" — this is the last human checkpoint
    // before submission, so an unlabeled positional list ("Acme · true · 2026-01-01")
    // would not be a verification. Booleans and choice values are humanised the same way
    // a top-level field's are.
    const cellText = (column, cell) => {
      if (cell === null || cell === undefined || cell === '') return '';
      if (column?.type === 'yesno' || column?.type === 'checkbox') {
        if (cell === true) return 'Yes';
        if (cell === false) return 'No';
      }
      if (Array.isArray(cell)) return cell.join('/');
      const options = Array.isArray(column?.options) ? column.options : [];
      const match = options.find((option) => option && option.value === cell);
      return match ? match.label || String(cell) : String(cell);
    };
    const rendered = value
      .map((row) => {
        if (!row || typeof row !== 'object') return '';
        const source = columns.length
          ? columns
          : Object.keys(row).map((key) => ({ path: key, label: key }));
        return source
          .map((column) => {
            const text = cellText(column, row[column.path]);
            return text ? `${column.label || column.path}: ${text}` : '';
          })
          .filter(Boolean)
          .join(' · ');
      })
      .filter(Boolean);
    return rendered.length ? rendered.join('; ') : '—';
  }
  if (Array.isArray(value)) {
    const joined = value
      .map((v) => (v === null || v === undefined ? '' : String(v)))
      .filter(Boolean)
      .join(', ');
    return joined || '—';
  }
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

/**
 * `custom.<slug>` from a human label, de-duplicated against paths already in use. Slugs
 * are stable rather than random so the stored path stays readable in the audit log.
 */
export function customFieldPath(label, takenPaths = []) {
  const slug =
    String(label || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 40) || 'field';
  const taken = new Set(takenPaths);
  let candidate = `${CUSTOM_PATH_PREFIX}${slug}`;
  let n = 2;
  while (taken.has(candidate)) {
    candidate = `${CUSTOM_PATH_PREFIX}${slug}_${n}`;
    n += 1;
  }
  return candidate;
}

/**
 * `custom.page.<slug>` from a human title, de-duplicated against every code and custom
 * section ID. The ID never changes when a page is later renamed, which keeps field
 * destinations and section overrides stable.
 */
export function customSectionId(title, takenIds = []) {
  const slug =
    String(title || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 40) || 'page';
  const taken = new Set(takenIds);
  let candidate = `${CUSTOM_SECTION_PREFIX}${slug}`;
  let n = 2;
  while (taken.has(candidate)) {
    candidate = `${CUSTOM_SECTION_PREFIX}${slug}_${n}`;
    n += 1;
  }
  return candidate;
}

/**
 * A custom page may be removed only after its fields are moved or deleted. We intentionally
 * block rather than silently reassigning answers: an administrator should choose the new
 * context for a field instead of a destructive action guessing where it belongs.
 */
export function canDeleteCustomSection(section) {
  return Boolean(section?.isCustom) && (!Array.isArray(section.fields) || section.fields.length === 0);
}

/**
 * The tombstone list for the Field Builder's "Deleted fields" panel: every code-shipped
 * field whose override carries `deleted: true`, resolved back to its coded definition so
 * the panel can show a meaningful label and home page. Tombstones for paths that no
 * longer exist in the code schema are ignored (a later deploy removed the field for real).
 */
export function deletedBaseFields(baseSections, layout) {
  const { overrides } = normalizeLayout(layout);
  const deleted = [];
  for (const section of baseSections) {
    for (const field of section.fields) {
      if (field.path && overrides[field.path]?.deleted === true) {
        deleted.push({
          path: field.path,
          label: field.label || field.text || field.path,
          type: field.type,
          sectionId: section.id,
          sectionTitle: section.title,
        });
      }
    }
  }
  return deleted;
}

/**
 * Normalizes an admin-edited option list before it is persisted: trims label and value,
 * drops rows missing either, and drops rows whose value duplicates an earlier row
 * (duplicate values make a stored answer ambiguous for the audit record — a radio would
 * render two rows checked at once). The ADD form enforces this shape at creation; this
 * makes the EDIT paths enforce the same contract at save time, so a half-typed or blanked
 * row can never reach the live form.
 */
export function sanitizeOptionRows(rows) {
  if (!Array.isArray(rows)) return [];
  const seen = new Set();
  const clean = [];
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const label = String(row.label ?? '').trim();
    const value = String(row.value ?? '').trim();
    if (!label || !value || seen.has(value)) continue;
    seen.add(value);
    clean.push({ ...row, label, value });
  }
  return clean;
}

/**
 * Sanitizes every admin-edited option list in a layout document before save.
 *
 * - A base-field override whose options sanitize to EMPTY loses the `options` key
 *   entirely, reverting that field to its code-shipped choices — an admin who blanks
 *   every row gets the safe default back, never an unsatisfiable required field that
 *   would block every submission firm-wide.
 * - A custom field keeps its sanitized list (its creation path guarantees it started
 *   valid, and the editors refuse to remove the last row).
 */
export function sanitizeLayoutOptions(layout) {
  const normalized = normalizeLayout(layout);
  const overrides = {};
  for (const [path, override] of Object.entries(normalized.overrides)) {
    let next = override;
    if (next && Array.isArray(next.options)) {
      const options = sanitizeOptionRows(next.options);
      if (options.length === 0) {
        const { options: _dropped, ...rest } = next;
        next = rest;
      } else {
        next = { ...next, options };
      }
    }
    // A people chooser's title filter: trimmed, de-duplicated, blanks dropped; the key
    // is removed entirely once emptied so "no filter" is absent config, not [].
    if (next && Array.isArray(next.titleFilter)) {
      const titleFilter = [...new Set(next.titleFilter.map((t) => String(t || '').trim()).filter(Boolean))];
      if (titleFilter.length === 0) {
        const { titleFilter: _dropped, ...rest } = next;
        next = rest;
      } else {
        next = { ...next, titleFilter };
      }
    }
    // A base repeating group's admin-edited columns get the same scrub as a custom
    // group's: blank/duplicate/unknown-type columns dropped, keys assigned. Emptied out
    // entirely, the override key is removed so the code-shipped columns come back rather
    // than persisting a repeatable with no columns — a dead control on the form.
    if (next && Array.isArray(next.fields)) {
      const fields = sanitizeSubFieldRows(next.fields);
      if (fields.length === 0) {
        const { fields: _dropped, ...rest } = next;
        next = rest;
      } else {
        next = { ...next, fields };
      }
    }
    overrides[path] = next;
  }
  const customFields = normalized.customFields.map((field) => {
    if (!field) return field;
    let next = field;
    if (Array.isArray(field.options)) {
      next = { ...next, options: sanitizeOptionRows(field.options) };
    }
    // A repeating group's columns get the same treatment: blank/duplicate/unknown-type
    // columns are dropped before they can reach the live form.
    if (field.type === 'repeatable') {
      next = { ...next, fields: sanitizeSubFieldRows(field.fields) };
    }
    return next;
  });
  return { ...normalized, overrides, customFields };
}
